import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { AuthShell } from "./auth-shell";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const missingToken = !token.trim();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api<{ ok: boolean }>("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reset password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Choose a New Password"
      description="Pick a password at least 8 characters long, then sign in again."
      footer={
        <p>
          <Link to="/login">Back to Sign In</Link>
        </p>
      }
    >
      {missingToken ? (
        <div className="space-y-4">
          <p className="text-sm text-rs-danger" role="alert">
            This reset link is missing or incomplete. Request a new one from the
            sign-in page.
          </p>
          <Button asChild className="rs-auth-submit rs-auth-submit-block">
            <Link to="/forgot-password">Request a New Link</Link>
          </Button>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit}>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New Password"
            aria-label="New Password"
          />
          <Input
            id="confirm"
            name="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm Password"
            aria-label="Confirm Password"
          />
          {error ? (
            <p className="text-sm text-rs-danger" role="alert">
              {error}
            </p>
          ) : null}
          <Button
            className="rs-auth-submit rs-auth-submit-block"
            type="submit"
            disabled={busy}
          >
            {busy ? "Saving…" : "Update Password"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
